import { hasFailed } from "@miragon/react-utils-api";
import React, { useCallback, useEffect, useState } from "react";
import QAButton from "../../components/Design/Controls/QAButton";
import QAButtonRow from "../../components/Design/Layout/QAButtonRow";
import useSnackbar from "../../components/Hooks/Snackbar/useSnackbar";
import SettingsForm from "../../components/Settings/Old/Form/SettingsForm";
import SettingsLabel from "../../components/Settings/Old/Form/SettingsLabel";
import SettingsTextField from "../../components/Settings/Old/Form/SettingsTextField";
import { useAppDispatch } from "../../state/Store";
import {
    ControllerAPI,
    CreateRoleCommand,
    CreateRoleCommandVariable,
    RoleRO,
    UpdateRoleCommand,
    UpdateRoleCommandVariable
} from "../../api/api";
import { updateRolesManually } from "../../state/RolesState";
import { makeStyles } from "@mui/styles";
import FunctionCodeEditor from "../../components/CodeEditor/FunctionCodeEditor";
import VariableDefinitionForm from "../../components/Variables/VariableDefinitionForm";

const PREFIX = `import { Page } from "playwright-core";
import { Variables, Utils } from "utils";

/**
  * Diese Funktion wird aufgerufen, um das Skript dieser Rolle auszuführen.
  * Es kann mit allen Seiteninhalten interagieren, um das gewünschte Verhalten
  * zu simulieren. 
  *
  * {page} ist die Referenz zur Browser-Seite, mit der das Skript arbeitet.
  * {variables} sind die Variablen, die dem Skript übergeben werden.
  */
export const run = async (page: Page, variables: Variables, utils: Utils): Promise<void> => {
`;
const SUFFIX = `
};
`;

interface Props {
    scenario: RoleRO | undefined;
}

const useStyles = makeStyles({
    editor: {
        border: "1px solid #CCC"
    }
});

const RoleForm: React.FC<Props> = props => {
    const snackbar = useSnackbar();
    const dispatch = useAppDispatch();
    const classes = useStyles();

    const { scenario } = props;

    const [group, setGroup] = useState("");
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [variables, setVariables] = useState<(CreateRoleCommandVariable & UpdateRoleCommandVariable)[]>([]);
    const [script, setScript] = useState("");
    const [initialized, setInitialized] = useState(false);
    const [isDisabled, setIsDisabled] = useState(true);

    useEffect(() => {
        if (scenario) {
            setGroup(scenario.group);
            setName(scenario.name);
            setDescription(scenario.description);
            setVariables(scenario.variables);
            setScript(scenario.script);
        }
        setInitialized(true);
        setIsDisabled(false);
    }, [scenario]);

    const onSave = useCallback(async () => {
        const request: CreateRoleCommand & UpdateRoleCommand = { group, name, description, script, variables };

        setIsDisabled(true);

        const response = scenario
            ? await ControllerAPI.updateRole(scenario.id, request)
            : await ControllerAPI.createRole(request);

        setIsDisabled(false);

        if (hasFailed(response)) {
            snackbar.onApiError(response);
        } else {
            dispatch(updateRolesManually({
                update: [response.result]
            }));
            snackbar.onSuccess("Rolle gespeichert.");
        }
    }, [group, name, description, script, variables, scenario, snackbar, dispatch]);

    return (
        <>

            <SettingsForm>

                <SettingsLabel>Allgemein</SettingsLabel>

                <SettingsTextField
                    label="Gruppe"
                    disabled={isDisabled}
                    onChanged={setGroup}
                    value={group} />

                <SettingsTextField
                    label="Name"
                    disabled={isDisabled}
                    onChanged={setName}
                    value={name} />

                <SettingsTextField
                    label="Beschreibung"
                    disabled={isDisabled}
                    onChanged={setDescription}
                    value={description} />

            </SettingsForm>

            <SettingsForm xlarge>

                <SettingsLabel>Variablen</SettingsLabel>

                <VariableDefinitionForm
                    disabled={isDisabled}
                    variables={variables}
                    onUpdate={setVariables} />

            </SettingsForm>

            <SettingsForm full>

                <SettingsLabel>Skript</SettingsLabel>

                {initialized && (
                    <FunctionCodeEditor
                        prefix={PREFIX}
                        suffix={SUFFIX}
                        availableVariables={variables}
                        onContentChanged={setScript}
                        height="640px"
                        width="100%"
                        value={script}
                        className={classes.editor}
                        language="typescript"
                        options={{
                            padding: {
                                top: 16,
                                bottom: 16
                            },
                            scrollBeyondLastLine: false,
                            wrappingIndent: "deepIndent",
                            minimap: {
                                enabled: false
                            }
                        }} />
                )}

            </SettingsForm>

            <SettingsForm>

                <QAButtonRow marginTop>

                    <QAButton
                        disabled={isDisabled}
                        title="Speichern"
                        onClicked={onSave} />

                </QAButtonRow>

            </SettingsForm>

        </>
    );
};

export default RoleForm;
